import { formatAnyPhoneNumber } from "@/lib/phone"
import { CallbackPhoneNumber, TaskTemplate, TwilioPhoneNumber } from "@/lib/vibrato-api"
import { Label, Radio, Select } from "flowbite-react"
import { ArrowLeft, Phone, ArrowUpRight, Copy } from "iconoir-react"
import React, { useContext, useEffect, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { VibratoClientContext } from "./contexts"
import { LoadingSpinner } from "../LoadingSpinner"

const EditTwilioPhoneNumber: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>()
  const navigate = useNavigate()
  const vibratoClient = useContext(VibratoClientContext)

  const [phoneNumber, setPhoneNumber] = useState<TwilioPhoneNumber | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [successMessage, setSuccessMessage] = useState("")

  const [callbackNumbers, setCallbackNumbers] = useState<CallbackPhoneNumber[]>([])
  const [taskTemplates, setTaskTemplates] = useState<TaskTemplate[]>([])
  const [configType, setConfigType] = useState<"none" | "callback" | "template">("none")
  const [selectedCallbackUuid, setSelectedCallbackUuid] = useState<string | null>(null)
  const [selectedTemplateUuid, setSelectedTemplateUuid] = useState<string | null>(null)

  useEffect(() => {
    document.title = "Vibrato - Edit Phone Number"

    const fetchData = async () => {
      if (!vibratoClient || !uuid) return

      try {
        setIsLoading(true)
        const [number, callbacks, templates] = await Promise.all([
          vibratoClient.getTwilioPhoneNumber(uuid),
          vibratoClient.getCallbackPhoneNumbers(),
          vibratoClient.getTaskTemplates()
        ])

        setPhoneNumber(number)
        setCallbackNumbers(callbacks)
        setTaskTemplates(templates)

        // Set initial configuration type and selection
        if (number.callback_phone_number) {
          setConfigType("callback")
          setSelectedCallbackUuid(number.callback_phone_number.uuid)
        } else if (number.task_template) {
          setConfigType("template")
          setSelectedTemplateUuid(number.task_template.uuid)
        } else {
          setConfigType("none")
        }
      } catch (error) {
        console.error("Error fetching data:", error)
        setErrorMessage("Failed to load phone number information.")
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [vibratoClient, uuid])

  const handleSave = async () => {
    if (!vibratoClient || !uuid) return

    try {
      setIsSaving(true)
      setErrorMessage("")
      setSuccessMessage("")

      // Determine which values to update based on the selected configuration type
      const callbackUuid = configType === "callback" ? selectedCallbackUuid : null
      const templateUuid = configType === "template" ? selectedTemplateUuid : null

      await vibratoClient.updateTwilioPhoneNumber(uuid, callbackUuid || undefined, templateUuid || undefined)
      setSuccessMessage("Phone number updated successfully!")

      // Refetch the phone number to get updated data
      const updatedNumber = await vibratoClient.getTwilioPhoneNumber(uuid)
      setPhoneNumber(updatedNumber)

      // Update local state to reflect the changes
      if (configType === "callback") {
        setSelectedTemplateUuid(null)
      } else if (configType === "template") {
        setSelectedCallbackUuid(null)
      } else {
        setSelectedCallbackUuid(null)
        setSelectedTemplateUuid(null)
      }
    } catch (error) {
      console.error("Error updating phone number:", error)
      setErrorMessage("Failed to update phone number configuration.")
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeletePhoneNumber = async () => {
    if (!vibratoClient || !uuid || !confirm("Are you sure you want to delete this phone number?")) return

    try {
      setIsSaving(true)
      await vibratoClient.deleteTwilioPhoneNumber(uuid)
      navigate("/phone-numbers", { replace: true })
    } catch (error) {
      console.error("Error deleting phone number:", error)
      setErrorMessage("Failed to delete phone number.")
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="h-full p-8 flex flex-col items-center justify-center">
        <LoadingSpinner className="w-8 h-8 text-purple-600 mb-4" />
        <p className="mt-4 text-gray-600">Loading phone number...</p>
      </div>
    )
  }

  return (
    <div className="h-full p-6 md:p-8 flex flex-col gap-6">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/phone-numbers" className="text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="p-3 bg-purple-100 rounded-xl">
          <Phone width="24" className="text-purple-600" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Edit Phone Number</h1>
          {phoneNumber && (
            <p className="text-gray-600">{formatAnyPhoneNumber(phoneNumber.country_code, phoneNumber.phone_number)}</p>
          )}
        </div>
      </div>

      {errorMessage && (
        <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded-md">
          <p className="text-red-700">{errorMessage}</p>
        </div>
      )}

      {successMessage && (
        <div className="p-4 bg-green-50 border-l-4 border-green-500 rounded-md mb-4">
          <p className="text-green-700">{successMessage}</p>
        </div>
      )}

      <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-all duration-200">
        <h2 className="text-xl font-bold mb-4">Phone Number Configuration</h2>
        <p className="text-gray-600 mb-6">
          Choose how you want to configure this number for inbound calls. You can either forward calls to a verified
          callback number or use a task template to handle calls with AI.
        </p>

        <div className="space-y-6">
          <div className="flex flex-col gap-4">
            <Label className="flex items-center gap-3">
              <Radio checked={configType === "none"} onChange={() => setConfigType("none")} />
              <div>
                <p className="font-medium">No configuration</p>
                <p className="text-sm text-gray-500">The phone number will not handle inbound calls.</p>
              </div>
            </Label>

            <div>
              <Label className={`flex items-center gap-3 ${callbackNumbers.length === 0 ? "opacity-80" : ""}`}>
                <Radio
                  name="configType"
                  value="callback"
                  checked={configType === "callback"}
                  onChange={() => setConfigType("callback")}
                  disabled={callbackNumbers.length === 0}
                />
                <div className="flex-1">
                  <p className={`font-medium ${callbackNumbers.length === 0 ? "text-gray-400" : ""}`}>
                    Forward to callback number
                  </p>
                  <p className={`text-sm ${callbackNumbers.length === 0 ? "text-gray-400" : "text-gray-500"}`}>
                    All inbound calls will be forwarded to your callback number.
                  </p>
                </div>
              </Label>

              {callbackNumbers.length === 0 && (
                <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="p-1 bg-blue-100 rounded-full">
                      <Phone className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-800">No callback numbers available</p>
                      <p className="text-sm text-blue-700 mt-1">
                        You need to add and verify a callback number before you can use this option.
                      </p>
                      <Link
                        to="/callback-numbers?add=true"
                        className="inline-flex items-center gap-1 mt-2 text-sm font-medium text-blue-800 hover:text-blue-900 underline decoration-blue-300 hover:decoration-blue-400 underline-offset-2"
                      >
                        Add callback number
                        <ArrowUpRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {configType === "callback" && callbackNumbers.length > 0 && (
                <div className="mt-4">
                  <Label htmlFor="callbackSelect" className="">
                    Select callback number
                  </Label>
                  <Select
                    id="callbackSelect"
                    value={selectedCallbackUuid || ""}
                    onChange={(e) => setSelectedCallbackUuid(e.target.value)}
                    disabled={callbackNumbers.length === 0}
                  >
                    <option value="" disabled>
                      Select a callback number
                    </option>
                    {callbackNumbers.map((callback) => (
                      <option key={callback.uuid} value={callback.uuid}>
                        {formatAnyPhoneNumber(callback.country_code, callback.phone_number)}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
            </div>

            <div>
              <Label className={`flex items-center gap-3 ${taskTemplates.length === 0 ? "opacity-80" : ""}`}>
                <Radio
                  name="configType"
                  value="template"
                  checked={configType === "template"}
                  onChange={() => setConfigType("template")}
                  disabled={taskTemplates.length === 0}
                />
                <div className="flex-1">
                  <p className={`font-medium ${taskTemplates.length === 0 ? "text-gray-400" : ""}`}>
                    Use a task template
                  </p>
                  <p className={`text-sm ${taskTemplates.length === 0 ? "text-gray-400" : "text-gray-500"}`}>
                    AI will answer the call and follow the selected task template&apos;s instructions.
                  </p>
                </div>
              </Label>

              {taskTemplates.length === 0 && (
                <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="p-1 bg-blue-100 rounded-full">
                      <Copy className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-800">No task templates available</p>
                      <p className="text-sm text-blue-700 mt-1">
                        Create a task template to define how AI should handle inbound calls.
                      </p>
                      <Link
                        to="/task-templates/new"
                        className="inline-flex items-center gap-1 mt-2 text-sm font-medium text-blue-800 hover:text-blue-900 underline decoration-blue-300 hover:decoration-blue-400 underline-offset-2"
                      >
                        Create task template
                        <ArrowUpRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {configType === "template" && taskTemplates.length > 0 && (
                <div className="mt-4">
                  <Label htmlFor="templateSelect">Select task template</Label>
                  <Select
                    id="templateSelect"
                    value={selectedTemplateUuid || ""}
                    onChange={(e) => setSelectedTemplateUuid(e.target.value)}
                    disabled={taskTemplates.length === 0}
                  >
                    <option value="" disabled>
                      Select a task template
                    </option>
                    {taskTemplates.map((template) => (
                      <option key={template.uuid} value={template.uuid}>
                        {template.taskName}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-between gap-4 pt-4 border-t border-gray-200">
            <button
              onClick={handleDeletePhoneNumber}
              disabled={isSaving}
              className={`px-5 py-2.5 border border-red-600 text-red-600 hover:bg-red-50 rounded-lg font-medium text-sm transition-colors ${
                isSaving ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              Delete Phone Number
            </button>
            <div className="flex gap-3">
              <Link
                to="/phone-numbers"
                className={`text-center ${
                  isSaving ? "opacity-50 cursor-not-allowed" : ""
                } text-gray-900 bg-white border border-gray-200 focus:outline-none hover:bg-gray-100 focus:ring-4 focus:ring-gray-200 font-medium rounded-lg text-sm px-5 py-2.5`}
              >
                Cancel
              </Link>
              <button
                onClick={handleSave}
                disabled={
                  isSaving ||
                  (configType === "callback" && !selectedCallbackUuid) ||
                  (configType === "template" && !selectedTemplateUuid)
                }
                className={`px-5 py-2.5 bg-linear-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 ${
                  isSaving ||
                  (configType === "callback" && !selectedCallbackUuid) ||
                  (configType === "template" && !selectedTemplateUuid)
                    ? "opacity-70 cursor-not-allowed"
                    : ""
                }`}
              >
                {isSaving ? (
                  <>
                    <LoadingSpinner className="w-4 h-4 text-white mr-2" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditTwilioPhoneNumber
